/**
 * S18 — Projects + Tasks + Milestones + Task Logs API client (P7/P11/P14 endpoints).
 */
import {
  GanttData,
  Project,
  type ProjectCancelInput,
  type ProjectFinancialBaselineInput,
  ProjectHealth,
  ProjectMetrics,
  ProjectMilestone,
  type ProjectMilestoneMarkReachedInput,
  type ProjectMilestoneUpsertInput,
  type ProjectMoveEntityInput,
  ProjectStage,
  type ProjectStageTransitionInput,
  ProjectStatusUpdate,
  type ProjectStatusUpdateInput,
  ProjectTask,
  ProjectTaskLog,
  type ProjectTaskLogUpsertInput,
  type ProjectTaskReorderInput,
  type ProjectTaskStatusInput,
  type ProjectUpsertInput,
} from '@staiter/shared';
import { VisibilityTransitionPreview } from '@staiter/shared/task-module';
import { z } from 'zod';
import { apiRequest } from './client';

export type ProjectListFilter = {
  stage?: string;
  customer_id?: string;
  health?: string;
  pm_user_id?: string;
  sales_order_id?: string;
  /** project-visibility (F6) — фильтр по видимости (CSV open,private под мультивыбор B1). */
  visibility?: string;
  /** projects-by-company P4 — фильтр по компании (агрегат). */
  entity_id?: string;
  status?: string;
  /** F10 (project-actions-parity §8.2-О3) — тумблер «Показывать архивные». */
  include_archived?: boolean;
  expected_end_from?: string;
  expected_end_to?: string;
  q?: string;
  limit?: number;
  offset?: number;
  /** projects-table-parity B1/F3 — серверная сортировка табличного вида. */
  sort?: string;
  order?: 'asc' | 'desc';
  /** projects-table-consistency B1 — rule-builder операторы (CSV мультивыбор + отрицание + empty). */
  stage_not?: string;
  health_not?: string;
  customer_empty?: boolean;
  end_date_empty?: boolean;
};

// F1 (projects→task-module merge) — список PM-проектов читается из task-module
// (`/task-module/projects?pm_only=true`). Ответ {projects} адаптируется к {items}.
// mapProjectRow отдаёт алиасы expected_*/updated_at → legacy Project schema проходит.
// `status` релаксирован: tm-status (planning/active/on_hold/...) ≠ legacy
// (open/completed/cancelled); страница использует stage/health, не status.
const TmListProject = Project.extend({
  status: z.string(),
  /**
   * B6 — `stage`/`health` у task-module-проекта NULLABLE по домену (PM-lifecycle
   * заводится не сразу), а этот экран требовал их непустыми. Одна такая строка
   * роняла разбор ВСЕГО массива, и список из 18 проектов рендерился пустым при
   * честных счётчиках в бакетах. Пустое значение — нормальное состояние записи,
   * а не повод спрятать её вместе с остальными.
   */
  stage: ProjectStage.nullable().default('planning'),
  health: ProjectHealth.nullable().default('on_track'),
});
/**
 * D3.6a (Z-F13) — сколько записей выпало на последнем разборе списка.
 *
 * Построчный разбор (B6) вылечил массовое падение: одна битая запись больше не
 * уносит выдачу целиком. Цена — тихий частичный успех: запись просто исчезала,
 * ни телеметрии, ни счёта. Пользователь видел «17 проектов» вместо восемнадцати
 * и не имел повода заподозрить, что чего-то не хватает.
 *
 * Модульная переменная, а не поле ответа: `TmProjectListResponse` разбирается
 * внутри `apiRequest`, наружу отдаётся уже трансформированный массив, и вернуть
 * счётчик через саму схему без переписывания контракта нельзя. Читается сразу
 * после `fetchProjects`, до следующего запроса.
 */
let lastDroppedRows = 0;
/** D8.2 (DR-F7) — след последней потери строк: показывается рядом с уведомлением. */
let lastDroppedTraceId: string | null = null;
/** Диагностика последнего разбора: сколько записей не прошло схему. */
export function getLastDroppedProjectRows(): number {
  return lastDroppedRows;
}

/** D8.2 (DR-F7) — след последней потери; `null`, если потерь не было. */
export function getLastDroppedTraceId(): string | null {
  return lastDroppedTraceId;
}

/**
 * Выпавшая строка обязана оставить след. Штатный канал `apiRequest` сюда не
 * достаёт: он шлёт телеметрию, когда падает ВЕСЬ разбор, а здесь внешняя схема
 * (`z.array(z.unknown())`) успешна всегда — падение спрятано внутри трансформа.
 * Fire-and-forget, как и в клиенте: диагностика не вправе ронять экран.
 */
function reportDroppedProjectRows(dropped: number, total: number, issues: z.ZodIssue[]): void {
  /**
   * D8.2 (DR-F7) — след РОЖДАЕТСЯ ЗДЕСЬ, а не приходит из ответа.
   *
   * §6.3 требует от ошибки повтор и след. Повтор был («Обновить»), следа не
   * было: телеметрия уходит fire-and-forget, ручка отвечает `{ok:true}` и
   * идентификатора не возвращает — руководитель не мог назвать поддержке, ЧТО
   * именно не открылось. Идентификатор генерирует клиент и отправляет его же
   * в теле: в серверном логе он ложится в `fe_trace_id`, то есть строка на
   * экране и запись в логе сходятся по одному числу. Ждать ответа ради этого
   * не нужно — след виден сразу и не зависит от того, дошла ли телеметрия.
   */
  lastDroppedTraceId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `fe-${Date.now().toString(36)}`;
  void fetch('/api/v1/telemetry/zod-error', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: '/task-module/projects (построчный разбор)',
      method: 'GET',
      issues: issues.slice(0, 10),
      severity: 'warn',
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      dropped_rows: dropped,
      total_rows: total,
      trace_id: lastDroppedTraceId,
    }),
  }).catch(() => {
    /* double-fail guard */
  });
}

const TmProjectListResponse = z.object({
  // Построчный разбор: битая запись выпадает одна, а не уносит выдачу.
  projects: z.array(z.unknown()).transform((rows) => {
    const out: z.infer<typeof TmListProject>[] = [];
    const dropped: z.ZodIssue[] = [];
    for (const raw of rows) {
      const r = TmListProject.safeParse(raw);
      if (r.success) out.push(r.data);
      else dropped.push(...r.error.issues);
    }
    lastDroppedRows = rows.length - out.length;
    if (lastDroppedRows === 0) lastDroppedTraceId = null;
    if (lastDroppedRows > 0) reportDroppedProjectRows(lastDroppedRows, rows.length, dropped);
    return out;
  }),
  // projects-by-company P1 — denorm имён компаний (entity_id → {name}) для чипа «Компания».
  entities: z
    .record(z.string(), z.object({ name: z.string() }))
    .optional()
    .default({}),
  // projects-table-parity B1/F5 — честный total_count по фильтрам (для бейджа «N из M»).
  // Схема НЕ .strict() — но поле объявляем явно, иначе молча отбросится (§6.2-L10).
  total_count: z.number().optional(),
});

function qs(filter: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter))
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  const s = params.toString();
  return s ? `?${s}` : '';
}

// ─── Projects ───────────────────────────────────────────────────────

export async function fetchProjects(filter: ProjectListFilter = {}) {
  const data = await apiRequest(
    `/task-module/projects${qs({ ...filter, pm_only: true })}`,
    TmProjectListResponse,
  );
  // status релаксирован в схеме (tm-enum) — структурно Project, кастуем под downstream-тип.
  return {
    items: data.projects as unknown as Project[],
    entities: data.entities,
    total_count: data.total_count,
    // D3.6a — число выпавших строк едет вместе с выдачей: экран обязан их назвать,
    // а не показывать «17 проектов» там, где сервер прислал восемнадцать.
    dropped_rows: getLastDroppedProjectRows(),
    dropped_trace_id: getLastDroppedTraceId(),
  };
}

// project-templates F1.2 — мёртвый `fetchProjectTemplates` (клиент обманки
// GET /projects/templates) удалён; реальные шаблоны — снапшоты в
// `lib/tasks/api/blueprints.ts`. Парный BE-ретайр — B2.7 (атомарный релиз).

// F2 (projects→task-module merge) — detail читается из task-module GET /:id
// ({project, members, status_updates, task_summary}). status релаксирован (tm-enum).
export async function fetchProject(id: string) {
  const data = await apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}`,
    z.object({
      project: Project.extend({ status: z.string() }),
      status_updates: z.array(ProjectStatusUpdate).optional(),
      task_summary: z
        .object({
          total: z.number(),
          completed: z.number(),
          overdue: z.number(),
          // F4-rework — сколько листьев участвовало в расчёте процента.
          // `.optional()` — forward-compat: бэкенд до выката поля не шлёт.
          leaf_count: z.number().optional(),
          // Z-B5 — разрез оценок по единицам в том же скоупе, что `leaf_count`.
          // Наружу идут код единицы и числа: фразу собирает фронт (п.19).
          // `uom: null` = задачи без оценки, их вес по §6 п.1 равен 1.
          estimate_uom_breakdown: z
            .array(
              z.object({
                uom: z.string().nullable(),
                task_count: z.number(),
                estimate_sum: z.string(),
              }),
            )
            .optional(),
        })
        .optional(),
    }),
  );
  return {
    ...data.project,
    ...(data.status_updates ? { status_updates: data.status_updates } : {}),
    ...(data.task_summary ? { task_summary: data.task_summary } : {}),
  } as Project & {
    status_updates?: typeof data.status_updates;
    task_summary?: typeof data.task_summary;
  };
}

// F6 — PM-проект создаётся/редактируется в task-module ({project} unwrap, status релакс).
export async function createProject(body: z.infer<typeof ProjectUpsertInput>) {
  const data = await apiRequest(
    '/task-module/projects/pm-create',
    z.object({ project: Project.extend({ status: z.string() }) }),
    { method: 'POST', body },
  );
  return data.project as unknown as Project;
}

export async function updateProject(id: string, body: Partial<z.infer<typeof ProjectUpsertInput>>) {
  const data = await apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/pm`,
    z.object({ project: Project.extend({ status: z.string() }) }),
    { method: 'PATCH', body },
  );
  return data.project as unknown as Project;
}

// project-visibility (F3/F4) — member-review переход видимости (B20).
export async function fetchVisibilityPreview(id: string): Promise<VisibilityTransitionPreview> {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/visibility-preview`,
    VisibilityTransitionPreview,
  );
}

export async function changeProjectVisibility(
  id: string,
  body: { visibility: 'open' | 'private'; exclude_user_ids?: string[] },
): Promise<Project> {
  const data = await apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/visibility`,
    z.object({ project: Project.extend({ status: z.string() }) }),
    { method: 'POST', body },
  );
  return data.project as unknown as Project;
}

// access-go-live 2026-07-08 — перенос проекта в другую компанию (отдельная операция
// с каскадом/guard-ами, не PATCH-поле; см. BACKLOG-2026-07-08-project-move-entity).
const ProjectMoveResponse = z.object({
  project: Project.extend({ status: z.string() }),
  moved: z
    .object({
      tasks: z.number().int(),
      dropped_dependencies: z.number().int(),
      dropped_label_links: z.number().int(),
    })
    .optional(),
});

export async function moveProjectEntity(id: string, body: ProjectMoveEntityInput) {
  const data = await apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/move-entity`,
    ProjectMoveResponse,
    { method: 'POST', body },
  );
  return { project: data.project as unknown as Project, moved: data.moved };
}

/**
 * F12 / PLM-6 (пак `tails-closeout`, B20; В-5) — что мешает закрыть проект. Диалог
 * закрытия показывает это до попытки, а не после отказа 422.
 */
export async function fetchProjectCloseout(id: string) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/closeout`,
    ProjectCloseoutPreview,
  );
}

// F2 — tm stage-transition возвращает {project} (unwrap); status релакс.
export async function transitionProjectStage(
  id: string,
  body: z.infer<typeof ProjectStageTransitionInput>,
) {
  const data = await apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/stage-transition`,
    z.object({ project: Project.extend({ status: z.string() }) }),
    { method: 'POST', body },
  );
  return data.project as unknown as Project;
}

export async function postProjectStatusUpdate(
  id: string,
  body: z.infer<typeof ProjectStatusUpdateInput>,
) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/status-update`,
    ProjectStatusUpdate,
    { method: 'POST', body },
  );
}

/** UX5 — Cancel project. F2/B6 — на tm, возвращает {project} (unwrap, status релакс). */
export async function cancelProject(id: string, body: z.infer<typeof ProjectCancelInput>) {
  const data = await apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/cancel`,
    z.object({ project: Project.extend({ status: z.string() }) }),
    { method: 'POST', body },
  );
  return data.project as unknown as Project;
}

export async function fetchProjectMetrics(id: string, refresh = false) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/metrics${refresh ? '?refresh=true' : ''}`,
    ProjectMetrics,
  );
}

export async function fetchProjectGantt(id: string) {
  return apiRequest(`/projects/${encodeURIComponent(id)}/gantt`, GanttData);
}

// FE-13 / BE-1 — Project activity feed. UNION 4 sources.
import { ProjectActivityResponse, ProjectCloseoutPreview } from '@staiter/shared';

export async function fetchProjectActivity(id: string, limit = 10) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/activity?limit=${limit}`,
    ProjectActivityResponse,
  );
}

// FE-14 / BE-2 — AI insights stub. AIInsightCard hide когда items пуст.
const AIInsightResponse = z.object({
  project_id: z.string().uuid(),
  items: z.array(
    z.object({
      id: z.string(),
      kind: z.string(),
      headline: z.string(),
      body: z.string(),
      confidence: z.number().nullable(),
      trace_id: z.string().nullable(),
      created_at: z.string(),
    }),
  ),
});
export type AIInsightResponse = z.infer<typeof AIInsightResponse>;

export async function fetchProjectAIInsights(id: string) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/ai-insights`,
    AIInsightResponse,
  );
}

// FE-73 / BE-13 — Baseline preview before persist (no Tier 2 gate yet).
const BaselinePreviewResponse = z.object({
  project_id: z.string().uuid(),
  old_margin: z.string(),
  new_margin: z.string(),
  delta: z.string(),
  variance_pct: z.string(),
  // Сравнимо ли отклонение вообще: при нулевой прежней марже знаменателя нет и
  // движок отдаёт заглушку ±200%, которую нельзя показывать как измеренную.
  variance_comparable: z.boolean(),
  // Основания согласования — СПИСОК структурных кодов: клик может менять сразу
  // и линейку, и плановую стоимость. Текст локализует фронт (п.19).
  tier2_bases: z.array(z.enum(['relative', 'first_baseline', 'material', 'method'])),
  requires_tier2: z.boolean(),
  // NC-39 (B48): выручка — операнд маржи, с источником.
  revenue: z
    .object({
      amount: z.string(),
      currency: z.string(),
      source: z.enum(['ar', 'none', 'form']),
    })
    .optional(),
});
export type BaselinePreviewResponse = z.infer<typeof BaselinePreviewResponse>;

export async function previewProjectBaseline(
  id: string,
  body: {
    new_estimated_cost: { amount: string; currency: string };
    new_billed?: { amount: string; currency: string };
    new_percent_complete_method?: string;
  },
) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/baseline-preview`,
    BaselinePreviewResponse,
    { method: 'POST', body },
  );
}

/**
 * Z-B1 (Н-9) — процент по ПЕРЕДАННОМУ методу без сохранения.
 *
 * Ручка была построена в заходе С5 ровно под строку «Прогресс: было X → станет
 * Y» в редакторе baseline, но вызывающего у неё не появилось: форма продолжала
 * писать «станет —, после сохранения пересчитается», то есть просила выбрать
 * правило, не показывая его результат. Считать это на клиенте нельзя —
 * слагаемые (`progress_sum`, `weighted_progress`, `weight_sum`) наружу не
 * отдаются осознанно.
 */
const PercentPreviewSide = z.object({
  method: z.string(),
  percent_complete: z.string(),
});
/**
 * D4.8 (CC-3) — запись batch-режима: процент + пригодность метода.
 *
 * `available` приезжает с бэка (D2.4) намеренно: `task_weight` при нулевой сумме
 * весов возвращает `'0'`, неотличимое от честного «ничего не сделано», а сами веса
 * наружу не отдаются. Без флага «метод не работает» пришлось бы угадывать по числу.
 */
const PercentPreviewEntry = z.object({
  method: z.string(),
  percent_complete: z.string(),
  available: z.boolean().optional().default(true),
  unavailable_reason: z.string().nullable().optional().default(null),
});
const PercentPreviewResponse = z.object({
  current: PercentPreviewSide,
  preview: PercentPreviewSide,
  // Отсутствует, когда спросили один метод (`?method=` без `methods=`).
  previews: z.array(PercentPreviewEntry).optional(),
});

export async function fetchProjectPercentPreview(
  id: string,
  opts: { method: string; manual_value?: string; methods?: readonly string[] },
) {
  const params = new URLSearchParams({ method: opts.method });
  // D4.8 — все методы одним запросом: список в форме показывает превью рядом с
  // каждым вариантом, а не только у выбранного. Четыре отдельных запроса при
  // открытии списка были той причиной, по которой превью не подключили вовсе.
  if (opts.methods && opts.methods.length > 0) params.set('methods', opts.methods.join(','));
  if (opts.manual_value !== undefined) params.set('manual_value', opts.manual_value);
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/percent-preview?${params.toString()}`,
    PercentPreviewResponse,
  );
}

// FE-70 / BE-12 — Project approvers picker (filtered by role + same entity + not-self).
const ProjectApproverItem = z.object({
  id: z.string().uuid(),
  email: z.string(),
  full_name: z.string().nullable(),
  role: z.string(),
});
/**
 * B10/B11 (project-plan-change §5) — почему кандидатов нет, и не запасной ли подписал.
 *
 * Оба поля ОПЦИОНАЛЬНЫ и приходят порознь: `no_candidates` только при пустом списке,
 * `escalation_step` только когда круг пришлось расширить. Форма непустого штатного
 * ответа не изменилась — фронт не разбирает два формата.
 */
const ProjectApproversResponse = z.object({
  items: z.array(ProjectApproverItem),
  escalation_step: z.enum(['matrix', 'company_admin', 'global_admin']).optional(),
  no_candidates: z
    .object({
      code: z.literal('NO_APPROVER_CANDIDATES'),
      required_roles: z.array(z.string()),
    })
    .optional(),
});
export type ProjectApproverItem = z.infer<typeof ProjectApproverItem>;
export type ProjectApproversResponse = z.infer<typeof ProjectApproversResponse>;

export async function fetchProjectApprovers(
  // B11/F7 — `project.archive` и `project.restore` в наборе: у архивации и возврата
  // теперь свои кандидаты в подписанты, объявленные approval-матрицей.
  opts: {
    action?:
      | 'project.pause'
      | 'project.complete'
      | 'project.cancel'
      | 'project.archive'
      | 'project.restore'
      // B8 — у переноса срока свои кандидаты, объявленные матрицей.
      | 'project.plan_shift';
    q?: string;
  } = {},
) {
  const params = new URLSearchParams();
  if (opts.action) params.set('action', opts.action);
  if (opts.q) params.set('q', opts.q);
  const query = params.toString();
  return apiRequest(
    `/task-module/projects/approvers${query ? `?${query}` : ''}`,
    ProjectApproversResponse,
  );
}

// FE-70 / BE-12 — Pause-impact preview (open tasks count + freed capacity + draft AR).
const PauseImpactResponse = z.object({
  project_id: z.string().uuid(),
  current_stage: z.string(),
  open_tasks_count: z.number(),
  draft_ar_invoices_count: z.number(),
  freed_capacity_hours: z.string(),
});
export type PauseImpactResponse = z.infer<typeof PauseImpactResponse>;

export async function fetchProjectPauseImpact(id: string) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/pause-impact`,
    PauseImpactResponse,
  );
}

/**
 * B5/F2 (project-actions-parity §2.3) — превью отмены.
 *
 * До него диалог отмены показывал `open_tasks_count` из `pause-impact` — счётчик,
 * посчитанный ДЛЯ ПАУЗЫ, с подписью «задачи останутся незакрытыми». После B3
 * отмена задачи закрывает, и старое обещание стало бы неправдой в другую сторону.
 * Четыре числа вместо одного: одно и то же «останется» имеет две разные причины,
 * и складывать их — значит скрыть ту, с которой пользователь может что-то сделать.
 */
const CancelPreviewResponse = z.object({
  project_id: z.string().uuid(),
  cancel_count: z.number(),
  keep_shared_count: z.number(),
  keep_no_state_count: z.number(),
  keep_done_count: z.number(),
  /** Уже отменённые — число для диалога ВОЗВРАТА: они обратно не откроются. */
  already_cancelled_count: z.number(),
});
export type CancelPreviewResponse = z.infer<typeof CancelPreviewResponse>;

export async function fetchProjectCancelPreview(id: string) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/cancel-preview`,
    CancelPreviewResponse,
  );
}

/**
 * B8/F8 — разотмена. Контракт тела тот же, что у отмены (обоснование + подпись):
 * асимметрия «уйти по подписи, вернуться молча» оставила бы журнал с половиной решений.
 */
/** F7 — превью судьбы задач при архивации (существовало, теперь есть и в диалоге). */
const ArchivePreviewResponse = z.object({
  promote_count: z.number(),
  archive_count: z.number(),
  detach_count: z.number(),
});

export async function fetchProjectArchivePreview(id: string) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/archive-preview`,
    ArchivePreviewResponse,
  );
}

/**
 * F7/B11 — архивация с обоснованием и подписью. До пака тела у этой ручки не было
 * вовсе: действие, трогающее два десятка задач, уходило по одному подтверждению.
 */
const ArchiveInput = z.object({
  reason: z.string().min(10).max(2000),
  // B10 (tier2-approval-requests §5) — поля подписи удалены: архивация и возврат
  // из архива уходят заявкой. Схема ручки на сервере строгая и лишний ключ отвергнет.
});

export async function archiveProject(id: string, body: z.infer<typeof ArchiveInput>) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}`,
    z.object({
      project: Project.extend({ status: z.string() }),
      promote_count: z.number(),
      archive_count: z.number(),
      detach_count: z.number(),
    }),
    { method: 'DELETE', body },
  );
}

// ─── F4/B6/B7 (project-plan-change) — перенос срока ─────────────────

/** Основания эскалации сдвига. Список, а не одно: перенос задевает сразу несколько. */
export const PlanShiftBasis = z.enum(['first_baseline', 'absolute', 'relative', 'backward']);
export type PlanShiftBasis = z.infer<typeof PlanShiftBasis>;

const PlanShiftPreview = z.object({
  /**
   * Перевёрнутый диапазон: конец плана раньше начала. Считает СЕРВЕР — тем же
   * кодом, что и мутация, иначе экран показал бы сдвиг там, где запись отвергнут.
   */
  range_invalid: z.boolean().default(false),
  /**
   * Величина ЭСКАЛАЦИИ — больший по модулю из двух сдвигов (PPC-F3, решение
   * владельца В-10). `null`, когда сравнивать не с чем — базовой линии нет. Не ноль.
   */
  shift_days: z.number().int().nullable(),
  /** Сдвиг окончания отдельно. */
  end_shift_days: z.number().int().nullable().default(null),
  /** Сдвиг начала отдельно; `null` — начала нет в базовой линии или в новом плане. */
  start_shift_days: z.number().int().nullable().default(null),
  baseline_start_date: z.string().nullable(),
  baseline_end_date: z.string().nullable(),
  baseline_duration_days: z.number().int().nullable(),
  bases: z.array(PlanShiftBasis),
  requires_signature: z.boolean(),
});
export type PlanShiftPreview = z.infer<typeof PlanShiftPreview>;

/**
 * B7 — что произойдёт ДО подтверждения. Считает сервер тем же кодом, что и мутация:
 * второй источник правды на фронте разошёлся бы с ним на первой правке порога.
 */
export async function fetchPlanShiftPreview(
  id: string,
  next: { start_date?: string | null; end_date?: string | null },
) {
  const params = new URLSearchParams();
  if (next.start_date !== undefined) params.set('start_date', next.start_date ?? '');
  if (next.end_date !== undefined) params.set('end_date', next.end_date ?? '');
  const qs = params.toString();
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/plan-shift-preview${qs ? `?${qs}` : ''}`,
    PlanShiftPreview,
  );
}

const PlanShiftInput = z.object({
  start_date: z.string().nullable().optional(),
  end_date: z.string().nullable().optional(),
  reason: z.string().min(10).max(500),
  /**
   * F1 (tier2-approval-requests) — вместо подписи адресат заявки.
   *
   * Поле не уходит на ручку переноса: оно говорит вызывающему, что сдвиг требует
   * согласования, и тот отправляет заявку (`submitProjectAction`). Ручка при Tier 2
   * прямой вызов не примет вовсе (B10).
   */
  addressee_user_id: z.string().uuid().optional(),
});
export type PlanShiftInput = z.infer<typeof PlanShiftInput>;

/** B6 — сам перенос. Базовая линия при этом не переписывается никогда. */
export async function shiftProjectPlan(id: string, body: PlanShiftInput) {
  // `addressee_user_id` — адресат ЗАЯВКИ, а не поле ручки: схема на сервере строгая,
  // лишний ключ она отвергнет. Прямой путь остаётся только для Tier 1.
  const { addressee_user_id: _addressee, ...rest } = body;
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/plan-baseline`,
    z.object({
      project: Project.extend({ status: z.string() }),
      shift_days: z.number().int().nullable(),
      bases: z.array(PlanShiftBasis),
    }),
    { method: 'PUT', body: rest },
  );
}

/** F9/B12 — возврат из архива. `partial` — правда о неполноте (SOLUTION §8.2-О1). */
export async function restoreProject(id: string, body: z.infer<typeof ArchiveInput>) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/restore`,
    z.object({
      project: Project.extend({ status: z.string() }),
      restored_task_count: z.number(),
      partial: z.boolean(),
    }),
    { method: 'POST', body },
  );
}

export async function uncancelProject(id: string, body: z.infer<typeof ProjectCancelInput>) {
  const data = await apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/uncancel`,
    z.object({ project: Project.extend({ status: z.string() }) }),
    { method: 'POST', body },
  );
  return data.project as unknown as Project;
}

// FE-86 / BE-19 — Project lifecycle journal (AuditEvents filtered by tool whitelist).
const StatusJournalItem = z.object({
  id: z.string().uuid(),
  occurred_at: z.string(),
  actor_user_id: z.string().nullable(),
  actor_role: z.string().nullable(),
  action: z.string(),
  tool_name: z.string(),
  tier: z.number().int(),
  request_payload: z.unknown(),
  response_summary: z.unknown(),
  status: z.string(),
});
const StatusJournalResponse = z.object({
  project_id: z.string().uuid(),
  items: z.array(StatusJournalItem),
  next_cursor: z.string().nullable(),
});
export type StatusJournalItem = z.infer<typeof StatusJournalItem>;

export async function fetchProjectStatusJournal(
  id: string,
  opts: { from?: string; to?: string; actor_id?: string; limit?: number; cursor?: string } = {},
) {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  if (opts.actor_id) params.set('actor_id', opts.actor_id);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);
  const query = params.toString();
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/status-journal${query ? `?${query}` : ''}`,
    StatusJournalResponse,
  );
}

// FE-74 / BE-13 — Baseline history (AuditEvents per project filtered by tool).
const BaselineHistoryItem = z.object({
  id: z.string().uuid(),
  created_at: z.string(),
  actor_user_id: z.string().nullable(),
  input: z.unknown(),
  output: z.unknown(),
  status: z.string(),
});
const BaselineHistoryResponse = z.object({
  project_id: z.string().uuid(),
  items: z.array(BaselineHistoryItem),
});
export type BaselineHistoryItem = z.infer<typeof BaselineHistoryItem>;

export async function fetchBaselineHistory(id: string) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/baseline-history`,
    BaselineHistoryResponse,
  );
}

// F2 — tm financial-baseline возвращает {project} (unwrap); status релакс.
export async function updateProjectFinancialBaseline(
  id: string,
  body: z.infer<typeof ProjectFinancialBaselineInput> & { reason: string },
) {
  const data = await apiRequest(
    `/task-module/projects/${encodeURIComponent(id)}/financial-baseline`,
    z.object({ project: Project.extend({ status: z.string() }) }),
    { method: 'PUT', body },
  );
  return data.project as unknown as Project;
}

// ─── Tasks ──────────────────────────────────────────────────────────

const ProjectTasksResponse = z.object({ items: z.array(ProjectTask) });

export async function fetchProjectTasks(
  projectId: string,
  opts: {
    mode?: 'flat' | 'tree';
    status?: string;
    assignee_user_id?: string;
    search?: string;
  } = {},
) {
  return apiRequest(
    `/projects/${encodeURIComponent(projectId)}/tasks${qs({ mode: 'flat', ...opts })}`,
    ProjectTasksResponse,
  );
}

export type ProjectTaskCreateBody = {
  subject: string;
  description?: string | null;
  status?: z.infer<typeof ProjectTask.shape.status>;
  is_milestone?: boolean;
  is_group?: boolean;
  parent_task_id?: string | null;
  exp_start_date?: string | null;
  exp_end_date?: string | null;
  expected_time_hours?: string;
  progress?: string;
  task_weight?: string;
  assignee_user_id?: string | null;
  milestone_id?: string | null;
  depends_on_task_ids?: string[];
};

export async function createProjectTask(projectId: string, body: ProjectTaskCreateBody) {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/tasks`, ProjectTask, {
    method: 'POST',
    body,
  });
}

export async function updateProjectTask(taskId: string, body: Partial<ProjectTaskCreateBody>) {
  return apiRequest(`/tasks/${encodeURIComponent(taskId)}`, ProjectTask, {
    method: 'PUT',
    body,
  });
}

export async function deleteProjectTask(taskId: string) {
  return apiRequest(`/tasks/${encodeURIComponent(taskId)}`, z.object({ ok: z.boolean() }), {
    method: 'DELETE',
  });
}

// FE-79 / BE-16 — Bulk delete tasks с literal «УДАЛИТЬ» confirm.
const BulkDeleteResponse = z.object({
  deleted_count: z.number().int(),
  requires_tier2: z.boolean(),
  project_id: z.string().uuid(),
});
export type BulkDeleteResponse = z.infer<typeof BulkDeleteResponse>;

export async function bulkDeleteProjectTasks(body: {
  task_ids: string[];
  confirmation: 'УДАЛИТЬ';
  project_id: string;
}) {
  return apiRequest('/tasks/bulk-delete', BulkDeleteResponse, {
    method: 'POST',
    body,
  });
}

// FE-77 / BE-15 — Last worklog entry for current user (for «Продолжить предыдущую» banner)
const LastTaskLogResponse = z.object({
  item: ProjectTaskLog.nullable(),
});
export async function fetchLastTaskLog() {
  return apiRequest('/task-logs/last', LastTaskLogResponse);
}

// FE-84 / BE-17 — Delete preview: subtree count + dependents + logged hours
const TaskDeletePreviewResponse = z.object({
  task_id: z.string().uuid(),
  project_id: z.string().uuid(),
  subject: z.string(),
  is_group: z.boolean(),
  subtask_count: z.number().int(),
  dependent_count: z.number().int(),
  logged_hours_total: z.string(),
  subtree_logged_hours: z.string(),
  has_subtree: z.boolean(),
});
export type TaskDeletePreviewResponse = z.infer<typeof TaskDeletePreviewResponse>;

export async function fetchTaskDeletePreview(taskId: string) {
  return apiRequest(
    `/tasks/${encodeURIComponent(taskId)}/delete-preview`,
    TaskDeletePreviewResponse,
  );
}

export async function reorderProjectTask(
  taskId: string,
  body: z.infer<typeof ProjectTaskReorderInput>,
) {
  return apiRequest(`/tasks/${encodeURIComponent(taskId)}/reorder`, z.object({ ok: z.boolean() }), {
    method: 'POST',
    body,
  });
}

export async function setProjectTaskStatus(
  taskId: string,
  body: z.infer<typeof ProjectTaskStatusInput>,
) {
  return apiRequest(
    `/tasks/${encodeURIComponent(taskId)}/status`,
    z.object({ ok: z.boolean(), new_status: z.string() }),
    { method: 'POST', body },
  );
}

export async function addProjectTaskDependency(taskId: string, dependsOnTaskId: string) {
  return apiRequest(
    `/tasks/${encodeURIComponent(taskId)}/depends-on`,
    z.object({ ok: z.boolean() }),
    { method: 'POST', body: { depends_on_task_id: dependsOnTaskId } },
  );
}

export async function removeProjectTaskDependency(taskId: string, dependsOnTaskId: string) {
  return apiRequest(
    `/tasks/${encodeURIComponent(taskId)}/depends-on/${encodeURIComponent(dependsOnTaskId)}`,
    z.object({ ok: z.boolean() }),
    { method: 'DELETE' },
  );
}

// ─── Milestones ─────────────────────────────────────────────────────

const MilestonesResponse = z.object({ items: z.array(ProjectMilestone) });

export async function fetchProjectMilestones(projectId: string) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(projectId)}/milestones`,
    MilestonesResponse,
  );
}

export async function createMilestone(
  projectId: string,
  body: z.infer<typeof ProjectMilestoneUpsertInput>,
) {
  return apiRequest(
    `/task-module/projects/${encodeURIComponent(projectId)}/milestones`,
    ProjectMilestone,
    {
      method: 'POST',
      body,
    },
  );
}

export async function updateMilestone(
  id: string,
  body: Partial<z.infer<typeof ProjectMilestoneUpsertInput>>,
) {
  return apiRequest(`/task-module/milestones/${encodeURIComponent(id)}`, ProjectMilestone, {
    method: 'PUT',
    body,
  });
}

export async function deleteMilestone(id: string) {
  return apiRequest(
    `/task-module/milestones/${encodeURIComponent(id)}`,
    z.object({ ok: z.boolean() }),
    {
      method: 'DELETE',
    },
  );
}

export async function markMilestoneReached(
  id: string,
  body: z.infer<typeof ProjectMilestoneMarkReachedInput> = {},
) {
  return apiRequest(
    `/task-module/milestones/${encodeURIComponent(id)}/mark-reached`,
    ProjectMilestone,
    {
      method: 'POST',
      body,
    },
  );
}

const DraftInvoiceResponse = z.object({
  ok: z.boolean(),
  invoice_id: z.string().uuid(),
  invoice_document_number: z.string(),
  amount: z.object({ amount: z.string(), currency: z.string() }),
});

export async function draftMilestoneInvoice(id: string) {
  return apiRequest(
    `/task-module/milestones/${encodeURIComponent(id)}/draft-invoice`,
    DraftInvoiceResponse,
    {
      method: 'POST',
      body: {},
    },
  );
}

// ─── Task Logs ──────────────────────────────────────────────────────

const TaskLogsResponse = z.object({ items: z.array(ProjectTaskLog) });

export async function fetchProjectTaskLogs(
  projectId: string,
  opts: { user_id?: string; task_id?: string; date_from?: string; date_to?: string } = {},
) {
  return apiRequest(
    `/projects/${encodeURIComponent(projectId)}/task-logs${qs(opts)}`,
    TaskLogsResponse,
  );
}

export async function createProjectTaskLog(
  projectId: string,
  body: z.infer<typeof ProjectTaskLogUpsertInput>,
) {
  return apiRequest(`/projects/${encodeURIComponent(projectId)}/task-logs`, ProjectTaskLog, {
    method: 'POST',
    body,
  });
}

export async function updateProjectTaskLog(
  id: string,
  body: Partial<z.infer<typeof ProjectTaskLogUpsertInput>>,
) {
  return apiRequest(`/task-logs/${encodeURIComponent(id)}`, ProjectTaskLog, {
    method: 'PUT',
    body,
  });
}

export async function deleteProjectTaskLog(id: string) {
  return apiRequest(`/task-logs/${encodeURIComponent(id)}`, z.object({ ok: z.boolean() }), {
    method: 'DELETE',
  });
}
